import React from "react";
import { bagiRata, cekBanyak, gabungAlokasi } from "@/lib/pembagian";

const inputCls = "rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100";

// One variasi line: which tailors, "bagi rata" with the leftover pieces chosen, or "atur sendiri".
function Baris({ baris, semua, mode, judul, banyak, onChange }) {
  const [pilih, setPilih] = React.useState(semua.map((p) => p.id));
  const [sisaKe, setSisaKe] = React.useState([]);
  const [atur, setAtur] = React.useState({});
  const nama = (id) => { const p = semua.find((x) => x.id === id); return p ? (p.nama || p.username) : id; };
  const rata = bagiRata(baris.pcs, pilih, sisaKe);
  const alokasi = mode === "RATA" ? rata.alokasi : pilih.map((id) => ({ operator_id: id, pcs: Number(atur[id] ?? 0) }));
  const key = JSON.stringify(alokasi);
  React.useEffect(() => { onChange(alokasi); }, [key]);  // eslint-disable-line react-hooks/exhaustive-deps
  const v = baris.variasi_no ?? "x";
  const t = banyak ? `${v}-` : "";  // one line: the test ids of T2-C stay as they were
  return (
    <div data-testid={`pb-baris-${v}`} className="space-y-1">
      {judul && <div className="font-semibold text-cyan-100">{judul}</div>}
      {semua.map((p) => (
        <label key={p.id} className="flex items-center gap-2">
          <input data-testid={`pb-pilih-${t}${p.username}`} type="checkbox" checked={pilih.includes(p.id)}
            onChange={(e) => setPilih(e.target.checked ? [...pilih, p.id] : pilih.filter((x) => x !== p.id))} />
          <span className="w-44 truncate">{p.nama || p.username}</span>
          {pilih.includes(p.id) && (mode === "RATA"
            ? <b data-testid={`pb-pcs-${t}${p.username}`}>{(rata.alokasi.find((a) => a.operator_id === p.id) || {}).pcs} pcs</b>
            : <input data-testid={`pb-atur-${t}${p.username}`} type="number" min="0" value={atur[p.id] ?? ""} onChange={(e) => setAtur({ ...atur, [p.id]: e.target.value })} className={`${inputCls} w-20`} />)}
        </label>
      ))}
      {mode === "RATA" && rata.sisa > 0 && (
        <div className="space-y-1">
          <div className="text-amber-200">{rata.dasar} pcs per orang, sisa {rata.sisa} pcs — pilih penerimanya:</div>
          {Array.from({ length: rata.sisa }).map((_, i) => (
            <label key={i} className="mr-3 text-xs text-slate-300">Sisa {i + 1}
              <select data-testid={`pb-sisa-${t}${i}`} value={sisaKe[i] || pilih[i % pilih.length]} onChange={(e) => { const n = [...sisaKe]; n[i] = e.target.value; setSisaKe(n); }} className={`${inputCls} ml-1`}>
                {pilih.map((id) => <option key={id} value={id}>{nama(id)}</option>)}
              </select></label>
          ))}
        </div>
      )}
    </div>
  );
}

// T2-C (owner 2026-10-08) + TARIF-JAHIT (T-2, 2026-10-10): "Bagi ke penjahit" in the Press form, per
// variasi line. `info` = {target, target_variasi: [{variasi_no, nama, pcs}], penjahit}; onChange(body | null, error | null).
export default function PembagianPress({ info, onChange }) {
  const semua = info.penjahit || [];
  const baris = info.target_variasi?.length ? info.target_variasi : [{ variasi_no: null, nama: null, pcs: info.target }];
  const [mode, setMode] = React.useState("RATA");
  const [alasan, setAlasan] = React.useState("");
  const [alok, setAlok] = React.useState({});
  const isi = baris.map((b) => ({ variasi_no: b.variasi_no, nama: b.nama, target: b.pcs, alokasi: alok[b.variasi_no ?? "x"] || [] }));
  const err = semua.length <= 1 ? null : cekBanyak(mode, isi, alasan);
  const kirim = semua.length <= 1 ? null : { mode, alokasi: gabungAlokasi(isi), alasan: mode === "ATUR" ? alasan : null };
  const key = JSON.stringify([kirim, err]);
  React.useEffect(() => { onChange(kirim, err); }, [key]);  // eslint-disable-line react-hooks/exhaustive-deps
  const banyak = baris.length > 1;

  if (!semua.length) return <p data-testid="pb-kosong" className="text-sm text-red-300">Tidak ada penjahit aktif — minta Owner mengaktifkan akun penjahit.</p>;
  if (semua.length === 1) return <p data-testid="pb-satu" className="text-sm text-cyan-200">Semua {info.target} pcs ke <b>{semua[0].nama || semua[0].username}</b> (satu-satunya penjahit aktif).</p>;
  return (
    <div data-testid="pembagian-press" className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 p-2 space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <b className="text-cyan-200">Bagi {info.target} pcs ke penjahit{banyak ? " — per baris variasi (upah sesuai yang dijahit)" : ""}</b>
        <label className="inline-flex items-center gap-1"><input data-testid="pb-rata" type="radio" checked={mode === "RATA"} onChange={() => setMode("RATA")} /> Bagi rata</label>
        <label className="inline-flex items-center gap-1"><input data-testid="pb-atur" type="radio" checked={mode === "ATUR"} onChange={() => setMode("ATUR")} /> Atur sendiri</label>
      </div>
      {baris.some((b) => b.tarif_belum) && <p data-testid="pb-tarif-belum" className="text-xs text-amber-300">⚠ Tarif jahit belum ada untuk {baris.filter((b) => b.tarif_belum).map((b) => b.nama || `variasi ${b.variasi_no}`).join(", ")} — tetap boleh dibagi; upah tercatat setelah Owner mengisi tarif.</p>}
      {baris.map((b) => (
        <Baris key={`${b.variasi_no ?? "x"}-${mode}`} baris={b} semua={semua} mode={mode} banyak={banyak}
          judul={banyak ? `Variasi ${b.variasi_no}${b.nama ? ` · ${b.nama}` : ""} — ${b.pcs} pcs` : null}
          onChange={(a) => setAlok((x) => ({ ...x, [b.variasi_no ?? "x"]: a }))} />
      ))}
      {mode === "ATUR" && <input data-testid="pb-alasan" value={alasan} maxLength={300} onChange={(e) => setAlasan(e.target.value)} placeholder="Alasan (wajib), mis. penjahit X izin" className={`${inputCls} w-full`} />}
      {err && <p data-testid="pb-error" className="text-xs text-amber-300">{err}</p>}
    </div>
  );
}
